/**
 * Stats Row Formatter
 *
 * Template-based E&T ("Events & Transactions") stats row system -- 5 tiles
 * (label + value each) plus one subtext line, rendered per client from
 * `clientData.language.et_*_template` config. Reuses the same template
 * engine `HeadlineFormatter`/`EquityCardFormatter` already established.
 *
 * Default template content and buildVariableMap's mapping logic are a
 * byte-identical migration of the legacy `userEquityTotalsMarkup` template
 * literal (src/shared_assets/index.js, `renderUserEquityModal`) -- see
 * docs/solutions/2026-09-16-et-stats-row-legacy-branch-audit.md for the
 * branch-by-branch source of truth every default here maps back to.
 *
 * =============================================================================
 * TEMPLATE SYNTAX (same as HeadlineFormatter/EquityCardFormatter)
 * =============================================================================
 *
 * 1. SIMPLE VARIABLE INTERPOLATION
 *    {{variableName}}
 *
 * 2. EXISTENCE CHECK / THRESHOLD CONDITIONAL (single-level only -- see note)
 *    {{#if variableName}}content{{/if}}
 *    {{#if variableName > threshold}}trueValue{{else}}falseValue{{/if}}
 *
 * 3. NUMBER FORMATTING
 *    {{variableName | dollars}}  - Rounded to whole dollars
 *    {{variableName | cents}}   - Dollars and cents (2 decimal places)
 *
 * 4. MATH EXPRESSIONS (left-to-right evaluation)
 *    {{var1 + var2}}, {{var1 - var2 - var3}}, etc.
 *
 * 5. NARROW-VIEWPORT CURRENCY ABBREVIATION
 *    When the `narrow` variable is truthy, any currency-typed value renders
 *    through K/M/B/T abbreviation (matching the legacy `numAbbr.abbreviate`
 *    usage in index.js's stats row) instead of `toLocaleString`.
 *
 * NOTE ON NESTING: `HeadlineFormatter.processConditionals`'s underlying
 * {{#if}} regex does not correctly resolve *nested* {{#if}}...{{else}}...{{/if}}
 * blocks (verified during this migration -- a nested else-branch gets
 * corrupted because the non-greedy regex closes on the innermost {{/if}}).
 * Every branch in the legacy markup with 3+ outcomes (or requiring true
 * nesting) is therefore resolved as a plain JS conditional inside
 * `buildVariableMap` and exposed as a single flat variable (`tile1Label`,
 * `tile3Value`, `tile4Value`, `tile5Label`, `tile5Value`, `capitalBalanceValue`,
 * `narrowMtClass`, `narrowTextXsClass`, `committedDisplayValue`) rather than
 * expressed as template-level {{#if}} chains. The one genuine single-level
 * {{#if}} left in a default template is `et_subtext_template`'s outer
 * `{{#if isFund}}` gate (#4485) -- its `narrow`/`useCommittedFallback`
 * sub-branches, which used to be their own single-level {{#if}}s, were
 * flattened into the three vars above precisely so the outer `isFund` gate
 * doesn't nest a second `{{#if}}` inside it.
 *
 * Unlike EquityCardFormatter, there is no path-based `user.`/`shares.`/
 * `config.`/`aggregate.` variable resolution here -- the stats row is a
 * portfolio-level aggregate, not per-class, so it has no per-class data to
 * resolve paths against.
 *
 * =============================================================================
 * AVAILABLE VARIABLES
 * =============================================================================
 *
 * Per-tile (built by buildVariableMap from renderUserEquityModal's locals):
 *   tile1Label..tile5Label  - Fully resolved label text for each tile ('html' --
 *                             trusted, may contain &nbsp; and unescaped
 *                             client-configured label text, matching legacy)
 *   tile1Value..tile5Value  - Fully resolved value for each tile (currency,
 *                             except tile4Value which is 'html' -- ROI/TVPI
 *                             display is not dollar-denominated)
 *   tile1State..tile5State  - 'content' | 'blank' | 'omitted' -- structural
 *                             tile visibility (e.g. isMAR's blank/omitted tiles)
 *
 * Source variables (lift-and-shift from the current locals, see KTD):
 *   contribution, distribution, gain, totalRealizedGain, totalReturnOfCapital,
 *   totalRedemption, currentValueTile, unfunded, totalWithholdings, committed,
 *   capitalBalanceValue, committedDisplayValue, zero
 *     - currency
 *   roiValue - raw numeric roi (already upstream-folded by the caller)
 *   labelGain, labelROI - client-configured label text (raw, escaped)
 *   narrowMtClass, narrowTextXsClass - precomputed subtext CSS-class strings
 *     (see NOTE ON NESTING above)
 *
 * Flags (used only in conditionals, not for display):
 *   isFund (from UFL), incGains, nowRedeeming, fullyRedeemed, narrow,
 *   showDecimals, USE_STRIKE, USE_TVPI, isJMS, isORR, isCER, isMAR, isTCA,
 *   labelGainIsUnrealized, useROILabel, useCommittedFallback
 *
 * subtext - the rendered subtext line (raw)
 *
 * =============================================================================
 */

import { HeadlineFormatter } from './HeadlineFormatter.js';
import { TemplateUtils } from '../helpers/TemplateUtils.js';
import NumAbbr from 'number-abbreviate';

// Matches the legacy `new NumAbbr(['K', 'M', 'B', 'T'])` instance used
// throughout index.js's narrow-viewport currency rendering.
const numAbbr = new NumAbbr(['K', 'M', 'B', 'T']);

// Legacy infinity glyph (index.js:17875/17878), used verbatim in two places.
const INFINITY_GLYPH = "<span style='font-size: 2rem; line-height: 120%;'>∞</span>";

const StatsRowFormatter = {
	// ========================================
	// VARIABLE TYPE DEFINITIONS
	// ========================================
	// Determines how each variable is formatted.

	variableTypes: {
		// Per-tile resolved label/value, referenced directly by defaultTemplates
		tile1Label: 'html',
		tile2Label: 'html',
		tile3Label: 'html',
		tile4Label: 'html',
		tile5Label: 'html',
		tile1Value: 'currency',
		tile2Value: 'currency',
		tile3Value: 'currency',
		tile4Value: 'html', // ROI/TVPI display -- infinity glyph, '%', or 'n.a.', not a dollar figure
		tile5Value: 'currency',

		// Subtext line
		subtext: 'raw',
		capitalBalanceValue: 'currency',
		committedDisplayValue: 'currency',
		narrowMtClass: 'raw',
		narrowTextXsClass: 'raw',
		zero: 'currency',

		// Source variables lifted from renderUserEquityModal's locals (KTD)
		contribution: 'currency',
		distribution: 'currency',
		gain: 'currency',
		totalRealizedGain: 'currency',
		totalReturnOfCapital: 'currency',
		totalRedemption: 'currency',
		currentValueTile: 'currency',
		unfunded: 'currency',
		totalWithholdings: 'currency',
		committed: 'currency',
		roiValue: 'percentage',
		labelGain: 'raw',
		labelROI: 'raw',

		// Boolean flags -- used only in conditionals, never interpolated directly
		isFund: 'raw',
		incGains: 'raw',
		nowRedeeming: 'raw',
		fullyRedeemed: 'raw',
		narrow: 'raw',
		showDecimals: 'raw',
		USE_STRIKE: 'raw',
		USE_TVPI: 'raw',
		isJMS: 'raw',
		isORR: 'raw',
		isCER: 'raw',
		isMAR: 'raw',
		isTCA: 'raw',
		labelGainIsUnrealized: 'raw',
		useROILabel: 'raw',
		useCommittedFallback: 'raw',

		// Per-tile structural state: 'content' | 'blank' | 'omitted'
		tile1State: 'raw',
		tile2State: 'raw',
		tile3State: 'raw',
		tile4State: 'raw',
		tile5State: 'raw',
	},

	// ========================================
	// DEFAULT TEMPLATES
	// ========================================
	// Used when no client override is configured (clientData.language.et_*_template).
	// Every default here reproduces a disposition documented in
	// docs/solutions/2026-09-16-et-stats-row-legacy-branch-audit.md.

	defaultTemplates: {
		et_tile1_label_template: '{{tile1Label}}',
		et_tile1_value_template: '{{tile1Value}}',
		et_tile2_label_template: '{{tile2Label}}',
		et_tile2_value_template: '{{tile2Value}}',
		et_tile3_label_template: '{{tile3Label}}',
		et_tile3_value_template: '{{tile3Value}}',
		et_tile4_label_template: '{{tile4Label}}',
		et_tile4_value_template: '{{tile4Value}}',
		et_tile5_label_template: '{{tile5Label}}',
		et_tile5_value_template: '{{tile5Value}}',
		// #4485: fund-language default renders only for isFund (UFL) clients; sub-branches stay flat per NOTE ON NESTING.
		et_subtext_template:
			'{{#if isFund}}<div class="pdf-inline">' +
			'<div class="mb-0 {{narrowMtClass}} md-border-b-1 pdf-text">' +
			'<div class="col s6">' +
			'<div class="center-align p-1 sm-p-1">' +
			'<span class="text-secondary {{narrowTextXsClass}} pdf-rep">Committed&nbsp;Capital: </span>' +
			'<span class="text-lg text-primary pdf-value"> {{committedDisplayValue}}</span>' +
			'</div>' +
			'</div>' +
			'</div>' +
			'<div class="mb-0 {{narrowMtClass}} md-border-b-1 pdf-text">' +
			'<div class="col s6">' +
			'<div class="center-align p-1 sm-p-1">' +
			'<span class="text-secondary {{narrowTextXsClass}} pdf-rep">Capital&nbsp;Balance: </span>' +
			'<span class="text-lg text-primary pdf-val">{{capitalBalanceValue}}</span>' +
			'</div>' +
			'</div>' +
			'</div>' +
			'</div>{{/if}}',
	},

	/**
	 * Format a single value based on its type
	 * @param {*} value - The value to format
	 * @param {string} type - 'currency', 'number', 'percentage', 'html', or 'raw'
	 * @param {string} [formatOverride] - Optional format override ('dollars', 'cents')
	 * @param {boolean} [narrow] - When true and type is 'currency', abbreviate (K/M/B/T)
	 *   instead of using toLocaleString -- matches the legacy narrow-viewport behavior.
	 * @param {boolean} [showDecimals] - Mirrors the legacy `stringOptions` UI
	 *   toggle (m0 vs m2): when true, non-narrow currency renders with 2
	 *   decimal places instead of the whole-dollar default.
	 * @param {number} [narrowDecPlaces] - decPlaces for narrow-viewport
	 *   abbreviation; defaults to 2. formatWithVars looks up a per-variable
	 *   `<varName>NarrowDecPlaces` override (see tile3ValueNarrowDecPlaces).
	 * @returns {string} - Formatted string (HTML-safe)
	 */
	formatValue: function (value, type, formatOverride, narrow, showDecimals, narrowDecPlaces) {
		if (value === undefined || value === null) return '';

		switch (type) {
			case 'currency': {
				if (typeof value !== 'number' || isNaN(value)) return '';
				if (narrow) {
					// Legacy rounds to 3 decimals before abbreviating (index.js:17814);
					// applied uniformly here since it's a no-op at abbreviation precision.
					const rounded = Math.round(value * 1000) / 1000;
					return '$' + numAbbr.abbreviate(rounded, narrowDecPlaces === undefined ? 2 : narrowDecPlaces);
				}
				const useCents = formatOverride === 'cents' || (formatOverride !== 'dollars' && !!showDecimals);
				return (
					'$' +
					value.toLocaleString('en-US', {
						minimumFractionDigits: useCents ? 2 : 0,
						maximumFractionDigits: useCents ? 2 : 0,
					})
				);
			}

			case 'number':
				if (typeof value !== 'number' || isNaN(value)) return '';
				return value.toLocaleString('en-US');

			case 'percentage':
				if (typeof value !== 'number' || isNaN(value)) return '';
				return value.toFixed(formatOverride === 'cents' ? 4 : 2) + '%';

			case 'html':
				// Trusted, formatter-generated markup (may contain &nbsp; or an
				// already-escaped/verbatim client label) -- never further escaped.
				return String(value);

			default:
				// Escape HTML for raw string values to prevent XSS
				return TemplateUtils.escapeHtml(String(value));
		}
	},

	/**
	 * Locale-format a number the same way legacy's
	 * `value.toLocaleString('en-US', stringOptions)` does, where stringOptions
	 * is m0 ({0,0} fraction digits) or m2 ({2,2}) depending on the showDecimals
	 * UI toggle.
	 * @private
	 */
	_formatLocale: function (value, showDecimals) {
		return value.toLocaleString('en-US', {
			minimumFractionDigits: showDecimals ? 2 : 0,
			maximumFractionDigits: showDecimals ? 2 : 0,
		});
	},

	/**
	 * Resolve the type to format a variable as, given an optional pipe modifier.
	 * @private
	 */
	_resolveType: function (varName, formatOverride, vars) {
		if (formatOverride) {
			if (formatOverride === 'dollars' || formatOverride === 'cents' || formatOverride === 'currency') return 'currency';
			if (formatOverride === 'number') return 'number';
			if (formatOverride === 'percentage') return 'percentage';
			if (formatOverride === 'html') return 'html';
			if (formatOverride === 'raw') return 'raw';
		}
		if (varName === 'roiValue' && vars) {
			return vars.USE_TVPI ? 'number' : 'percentage';
		}
		return this.variableTypes[varName] || 'raw';
	},

	/**
	 * Format a template string with variables.
	 * Uses TemplateUtils for shared filter/math processing and
	 * HeadlineFormatter.processConditionals for {{#if}} blocks, exactly as
	 * EquityCardFormatter.formatWithVars does. No path-based variable
	 * resolution -- the stats row has no per-class data.
	 *
	 * @param {string} template - Template string with {{variables}}
	 * @param {Object} vars - Variable map (from buildVariableMap)
	 * @param {Object} [options]
	 * @param {boolean} [options.forceNoAbbreviate] - When true, currency values
	 *   never abbreviate via numAbbr regardless of vars.narrow -- used by
	 *   renderSubtext, since narrow still drives the subtext's CSS classes
	 *   (mt-6/text-xs) but never its dollar-value formatting (per the audit).
	 * @returns {string} - Formatted string
	 */
	formatWithVars: function (template, vars, options) {
		if (!template) return '';

		const self = this;
		const opts = options || {};
		const narrow = opts.forceNoAbbreviate ? false : !!(vars && vars.narrow);
		const showDecimals = !!(vars && vars.showDecimals);

		// Step 1: Process conditionals ({{#if var}}...{{/if}}) -- single-level only, see file header
		template = HeadlineFormatter.processConditionals(template, vars);

		// Step 2: Process format modifiers and filters {{varName | modifier}}
		template = template.replace(/\{\{(\w+)\s*\|\s*(\w+)\}\}/g, function (_match, varName, modifier) {
			let value = TemplateUtils.safeGetVar(vars, varName);

			// Check if it's a filter first
			if (TemplateUtils.filters[modifier]) {
				value = TemplateUtils.applyFilter(value, modifier);
				if (modifier === 'toNumber' && typeof value === 'number') {
					return self.formatValue(value, 'number');
				}
				return self.formatValue(value, 'raw');
			}

			// Otherwise treat as format modifier
			const type = self._resolveType(varName, modifier, vars);
			return self.formatValue(value, type, modifier, narrow, showDecimals, vars && vars[`${varName}NarrowDecPlaces`]);
		});

		// Step 3: Process math expressions {{var1 + var2}}, etc.
		template = TemplateUtils.processMathExpressions(template, vars, function (result, modifier) {
			return self.formatValue(result, 'currency', modifier, narrow, showDecimals);
		});

		// Step 4: Process simple variable interpolation {{varName}}
		return template.replace(/\{\{(\w+)\}\}/g, function (_match, varName) {
			const value = TemplateUtils.safeGetVar(vars, varName);
			const type = self._resolveType(varName, undefined, vars);
			return self.formatValue(value, type, undefined, narrow, showDecimals, vars && vars[`${varName}NarrowDecPlaces`]);
		});
	},

	/**
	 * Get template with fallback chain:
	 * 1. Client override (clientData.language[templateKey])
	 * 2. Built-in default (this.defaultTemplates[templateKey])
	 *
	 * @param {string} templateKey - e.g. 'et_tile1_label_template', 'et_subtext_template'
	 * @param {Object} languageConfig - clientData.language
	 * @returns {string} - Template to use
	 */
	getTemplate: function (templateKey, languageConfig) {
		const override = languageConfig?.[templateKey];
		if (override) return override;
		return this.defaultTemplates[templateKey] || '';
	},

	/**
	 * Get the default template for a given key (for admin UI display, mirroring
	 * EquityCardService.getDefaultTemplate).
	 * @param {string} templateKey
	 * @returns {string}
	 */
	getDefaultTemplate: function (templateKey) {
		return this.defaultTemplates[templateKey] || '';
	},

	/**
	 * Render one tile (1-5), checking its structural state first.
	 * @param {number} n - Tile number, 1-5
	 * @param {Object} languageConfig - clientData.language
	 * @param {Object} vars - Variable map from buildVariableMap
	 * @returns {{state: string, label: (string|null), value: (string|null)}}
	 *   'omitted' -> label/value are both null (render nothing at all -- no div)
	 *   'blank'   -> label/value are both '' (render an empty spacer)
	 *   'content' -> label/value are the rendered template output
	 */
	renderTile: function (n, languageConfig, vars) {
		const state = (vars && vars[`tile${n}State`]) || 'content';

		if (state === 'omitted') {
			return { state, label: null, value: null };
		}
		if (state === 'blank') {
			return { state, label: '', value: '' };
		}

		const label = this.formatWithVars(this.getTemplate(`et_tile${n}_label_template`, languageConfig), vars);
		const value = this.formatWithVars(this.getTemplate(`et_tile${n}_value_template`, languageConfig), vars);
		return { state: 'content', label, value };
	},

	/**
	 * Render the subtext line. Checks `clientData.language.subtext_ET` first
	 * (raw literal text, unconditional wrapper markup, hidden if under 6
	 * characters -- completely unchanged legacy back-compat semantics), and
	 * only falls through to the templated `et_subtext_template` default when
	 * no override is configured.
	 * @param {Object} languageConfig - clientData.language
	 * @param {Object} vars - Variable map from buildVariableMap (vars.subtextForET
	 *   carries the raw clientData.language.subtext_ET value, if any)
	 * @returns {string} - Rendered subtext (empty string if hidden/empty)
	 */
	renderSubtext: function (languageConfig, vars) {
		const override = vars && vars.subtextForET;
		if (override) {
			// Raw literal text, not run through the template engine -- matches
			// legacy's un-escaped `${subtextForET}` interpolation exactly.
			if (String(override).length < 6) return '';
			return (
				'<div class="row mb-0 mt-0 md-border-b-1">' +
				'<div class="col right mr-8">' +
				'<div class="center-align p-1 sm-p-1">' +
				'<span class="text-secondary text-xs">' +
				String(override) +
				'</span>' +
				'</div>' +
				'</div>' +
				'</div>'
			);
		}

		const template = this.getTemplate('et_subtext_template', languageConfig);
		if (!template) return '';
		// narrow never abbreviates the subtext's dollar values (audit) -- only
		// its mt-6/text-xs classes, which read vars.narrow directly, unaffected.
		return this.formatWithVars(template, vars, { forceNoAbbreviate: true });
	},

	/**
	 * Build a variable map from the locals renderUserEquityModal already
	 * computes today -- a lift-and-shift, not a recalculation. No new math is
	 * introduced here (keeps #4476/#5384's fixes, which live in that upstream
	 * math, untouched). Every 3+-way legacy branch is resolved as a plain JS
	 * conditional here (not a template-level {{#if}} chain) because nested
	 * {{#if}} blocks are not reliably supported by the underlying engine --
	 * see the file header note.
	 *
	 * @param {Object} locals
	 * @param {number} [locals.totalContribution]
	 * @param {number} [locals.totalCashDistribution]
	 * @param {number} [locals.totalUnrealizedGain]
	 * @param {number} [locals.totalRealizedGain]
	 * @param {number} [locals.totalReturnOfCapital]
	 * @param {number} [locals.totalRedemption]
	 * @param {number} [locals.roi] - Already upstream-folded (IGNORE_DISTRIBUTIONS/
	 *   isMAZ/altRoi/USE_TVPI's `1 + roi/100` transform all happen before this call)
	 * @param {number} [locals.currentValue]
	 * @param {number} [locals.totalWithholdings]
	 * @param {number} [locals.unfunded]
	 * @param {number} [locals.committed]
	 * @param {boolean} [locals.fullyRedeemed]
	 * @param {boolean} [locals.nowRedeeming]
	 * @param {boolean} [locals.incGains]
	 * @param {string} [locals.labelGain]
	 * @param {string} [locals.labelROI]
	 * @param {boolean} [locals.USE_STRIKE]
	 * @param {boolean} [locals.USE_TVPI]
	 * @param {boolean} [locals.UFL] - Use Fund Language; mapped to `isFund`
	 * @param {boolean} [locals.narrow] - screen.width < 600
	 * @param {boolean} [locals.showDecimals] - legacy `stringOptions` UI toggle (m0 vs m2)
	 * @param {boolean} [locals.isJMS]
	 * @param {boolean} [locals.isORR]
	 * @param {boolean} [locals.isCER]
	 * @param {boolean} [locals.isMAR]
	 * @param {boolean} [locals.isTCA] - client == 'theclosingagentllc' (a strict
	 *   equality check, distinct from and narrower than the pre-existing
	 *   window.isTCA client-flags.js flag, which also matches 'theclosingagent')
	 * @param {string} [locals.subtextForET] - clientData.language.subtext_ET, if set
	 * @param {Object} [locals.tileStates] - Explicit per-tile state overrides, e.g.
	 *   { tile1: 'blank', tile4: 'omitted' }. Applied on top of the states
	 *   `isMAR` itself derives (tile1: blank, tile4: omitted when isMAR).
	 *   Unspecified tiles default to 'content'.
	 * @returns {Object} - Flat map of variable names to values
	 */
	buildVariableMap: function (locals = {}) {
		const num = (v) => (typeof v === 'number' && !isNaN(v) ? v : Number(v) || 0);
		const bool = (v) => !!v;

		const contribution = num(locals.totalContribution);
		const distribution = num(locals.totalCashDistribution);
		const gain = num(locals.totalUnrealizedGain);
		const totalRealizedGain = num(locals.totalRealizedGain);
		const totalReturnOfCapital = num(locals.totalReturnOfCapital);
		const totalRedemption = num(locals.totalRedemption);
		const roi = num(locals.roi);
		const currentValueTile = num(locals.currentValue);
		const totalWithholdings = num(locals.totalWithholdings);
		const unfunded = num(locals.unfunded);
		const committed = num(locals.committed);

		const incGains = bool(locals.incGains);
		const nowRedeeming = bool(locals.nowRedeeming);
		const fullyRedeemed = bool(locals.fullyRedeemed);
		const USE_STRIKE = bool(locals.USE_STRIKE);
		const USE_TVPI = bool(locals.USE_TVPI);
		const isFund = bool(locals.UFL);
		const narrow = bool(locals.narrow);
		const showDecimals = bool(locals.showDecimals);
		const isJMS = bool(locals.isJMS);
		const isORR = bool(locals.isORR);
		const isCER = bool(locals.isCER);
		const isMAR = bool(locals.isMAR);
		const isTCA = bool(locals.isTCA);
		const labelGain = locals.labelGain || '';
		const labelROI = locals.labelROI || '';
		const subtextForET = locals.subtextForET || null;

		// Flattened multi-way conditions the engine can't express (string
		// .includes(), 3-way ORs) -- see audit doc.
		const labelGainIsUnrealized = labelGain.includes('Unrealized');
		const useROILabel = incGains || isCER || isTCA;
		const useCommittedFallback = committed < contribution || contribution < 0;

		// --- Tile 1: label is a 4-way branch (incGains x isJMS, !incGains x nowRedeeming) ---
		const tile1Label = incGains ? (isJMS ? 'Capital Account' : 'Investment') : nowRedeeming ? 'Redemptions' : 'Contributions';
		const tile1Value = contribution;

		// --- Tile 2: genuinely 2-way, but kept as flat vars for uniformity with the other tiles ---
		const tile2Label = incGains ? 'Realized&nbsp;Return' : 'Distributions';
		const tile2Value = incGains ? totalRealizedGain : distribution;

		// --- Tile 3: label 2-way; value is a 3-way branch (labelGainIsUnrealized > incGains+USE_STRIKE > else) ---
		const tile3Label = incGains ? labelGain : 'Returns&nbsp;of&nbsp;Capital';
		const tile3Value = labelGainIsUnrealized ? gain : incGains ? gain - (USE_STRIKE ? totalRealizedGain : 0) : totalReturnOfCapital;
		// Legacy called numAbbr.abbreviate(totalUnrealizedGain) with no decPlaces
		// arg (defaults to 0) only in this branch -- every sibling tile passes 2.
		const tile3ValueNarrowDecPlaces = labelGainIsUnrealized ? 0 : 2;

		// --- Tile 4: label 2-way (flattened OR); value has the infinity-glyph/
		// isFinite/USE_TVPI-format-switch logic the engine can't express ---
		const tile4Label = useROILabel ? labelROI : '%&nbsp;of&nbsp;Contributions';
		let tile4Value;
		if (contribution < 0) {
			tile4Value = INFINITY_GLYPH;
		} else if (incGains) {
			if (isFinite(roi)) {
				tile4Value = USE_TVPI ? roi.toFixed(2) : this._formatLocale(roi, showDecimals) + '%';
			} else {
				tile4Value = INFINITY_GLYPH;
			}
		} else if (contribution > 0) {
			const pct = (100 * (distribution + totalReturnOfCapital)) / contribution;
			tile4Value = this._formatLocale(pct, showDecimals) + '%';
		} else {
			tile4Value = 'n.a.';
		}

		// --- Tile 5: label/value are both 4-way branches (incGains x isORR/USE_STRIKE, !incGains x totalWithholdings/fullyRedeemed) ---
		let tile5Label;
		let tile5Value;
		if (incGains) {
			tile5Label = isORR ? 'Estimated&nbsp;FMV' : USE_STRIKE ? 'Potential&nbsp;Value' : 'Current&nbsp;Value';
			tile5Value = currentValueTile;
		} else if (totalWithholdings > 0) {
			tile5Label = 'Taxes&nbsp;Withheld';
			tile5Value = totalWithholdings;
		} else if (fullyRedeemed) {
			tile5Label = 'Redemptions';
			tile5Value = totalRedemption;
		} else {
			tile5Label = 'Unfunded';
			tile5Value = unfunded;
		}

		// --- Subtext capital-balance value: 3-way branch ---
		const capitalBalanceValue = fullyRedeemed
			? 0
			: incGains
				? totalRealizedGain > contribution
					? 0
					: contribution - totalRealizedGain
				: totalReturnOfCapital > contribution
					? 0
					: contribution - totalReturnOfCapital;

		// --- Subtext: narrow/useCommittedFallback flattened to flat vars, see NOTE ON NESTING ---
		const narrowMtClass = narrow ? 'mt-6' : 'mt-0';
		const narrowTextXsClass = narrow ? 'text-xs' : '';
		const committedDisplayValue = useCommittedFallback ? contribution + unfunded : committed;

		const vars = {
			contribution,
			distribution,
			gain,
			totalRealizedGain,
			totalReturnOfCapital,
			totalRedemption,
			roiValue: roi,
			currentValueTile,
			totalWithholdings,
			unfunded,
			committed,
			zero: 0,
			capitalBalanceValue,
			committedDisplayValue,
			narrowMtClass,
			narrowTextXsClass,

			incGains,
			nowRedeeming,
			fullyRedeemed,
			USE_STRIKE,
			USE_TVPI,
			isFund,
			narrow,
			showDecimals,
			isJMS,
			isORR,
			isCER,
			isMAR,
			isTCA,
			labelGain,
			labelROI,
			subtextForET,
			labelGainIsUnrealized,
			useROILabel,
			useCommittedFallback,

			tile1Label,
			tile1Value,
			tile2Label,
			tile2Value,
			tile3Label,
			tile3Value,
			tile3ValueNarrowDecPlaces,
			tile4Label,
			tile4Value,
			tile5Label,
			tile5Value,
		};

		// isMAR structurally blanks tile1 and omits tile4 (see audit); explicit
		// tileStates overrides (if supplied) win over the isMAR-derived defaults.
		const derivedStates = {
			tile1: isMAR ? 'blank' : 'content',
			tile2: 'content',
			tile3: 'content',
			tile4: isMAR ? 'omitted' : 'content',
			tile5: 'content',
		};
		const tileStates = Object.assign(derivedStates, locals.tileStates || {});
		for (let n = 1; n <= 5; n++) {
			vars[`tile${n}State`] = tileStates[`tile${n}`] || 'content';
		}

		return vars;
	},
};

export { StatsRowFormatter };
